// /customers/:id — Customer 360 (PAGES.md §3.30 + §2.8)
//
// Hero profile + 4 KPI tiles + 5 tabs:
//   Profile  — editable demographic + contact details
//   Purchases — paginated bill history
//   Loyalty  — points balance, earn/redeem ledger, adjust points
//   Credits  — store credit ledger
//   Communications — sent invoices/OTPs/campaigns

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Tabs,
  Button,
  Input,
  TextArea,
  Select,
  Modal,
  RadioGroup,
  ConfirmDialog,
  DatePicker,
  Skeleton,
  EmptyState,
  Badge,
  Toggle,
  useToast,
} from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { ApiError } from '@/lib/api-client';
import { calendarDay } from '@/lib/store-clock';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  useCustomer,
  useCustomerLoyalty,
  useCustomerPurchases,
  useCustomerStoreCredit,
  useCustomerCommunications,
  useCustomerVoucherRedemptions,
  useUpdateCustomer,
  useAdjustLoyaltyPoints,
  useSetCustomerActive,
  useSetCustomerConsent,
  MergeCustomerModal,
  CustomerHeroProfile,
  CustomerPurchaseTable,
  LoyaltyLedgerTable,
  CreditLedgerTable,
  CommunicationLogTable,
  VoucherRedemptionTable,
  formatShortDate,
  isValidE164,
  isValidEmail,
  toE164,
} from '@/features/customers';
import { ReportKpiRow, KpiIcons } from '@/features/reports';
import {
  useMembershipPlanList,
  useCustomerActiveMembership,
  useEnrollCustomerInPlan,
  useCancelCustomerEnrollment,
  MembershipTierPill,
  formatDuration,
  formatTierLabel,
} from '@/features/memberships';
import type { EnrollCustomerInput } from '@/features/memberships';

type TabId = 'profile' | 'purchases' | 'loyalty' | 'credits' | 'vouchers' | 'memberships' | 'communications';

const TABS: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'purchases', label: 'Purchases' },
  { id: 'loyalty', label: 'Loyalty' },
  { id: 'credits', label: 'Credits' },
  { id: 'vouchers', label: 'Vouchers' },
  { id: 'memberships', label: 'Memberships' },
  { id: 'communications', label: 'Comms' },
];

interface ProfileFormState {
  customer_type: 'INDIVIDUAL' | 'CORPORATE';
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  date_of_birth: Date | undefined;
  anniversary: Date | undefined;
  address: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
  notes: string;
  tags: string[];
  tagInput: string;
  // Corporate-only
  company_name: string;
  company_address: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
}

function CustomerDetailContent({ id }: { id: string }) {
  const dial = useTenantDial();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabId) || 'profile';
  const { toast } = useToast();
  const currency = useTenantCurrency();
  // Statutory tax-id vocabulary/validation derives from the immutable currency.
  // INR → GST_INDIA: label `GSTIN`, GSTIN regex (byte-identical to before).
  // GCC → `TRN`/`VATIN`/… + 15-digit regex; Qatar/Kuwait have no statutory id
  // (`taxIdLabel === null`) ⇒ the field is hidden entirely.
  const tax = useTaxRegime();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwnerPlus = roleLevel >= ROLE_LEVELS.OWNER;
  const isManagerPlus = roleLevel >= ROLE_LEVELS.MANAGER;

  const customerQ = useCustomer(id);
  const loyaltyQ = useCustomerLoyalty(activeTab === 'loyalty' ? id : undefined);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const purchasesQ = useCustomerPurchases(activeTab === 'purchases' ? id : undefined, purchasesPage);
  const creditsQ = useCustomerStoreCredit(activeTab === 'credits' ? id : undefined);
  const vouchersQ = useCustomerVoucherRedemptions(activeTab === 'vouchers' ? id : undefined);
  const commsQ = useCustomerCommunications(activeTab === 'communications' ? id : undefined);

  // Active membership plans — only fetched when the Memberships tab opens.
  // Tenants usually have <10 plans so a single unpaginated call is fine.
  const activePlansQ = useMembershipPlanList(
    activeTab === 'memberships' ? { status: 'ACTIVE', limit: 100 } : { limit: 0 },
  );
  // Current active membership for this customer (null if none).
  const activeMembershipQ = useCustomerActiveMembership(activeTab === 'memberships' ? id : undefined);
  const enrollMutation = useEnrollCustomerInPlan(id);
  const cancelMembership = useCancelCustomerEnrollment(id);

  const updateMutation = useUpdateCustomer(id);
  const adjustLoyalty = useAdjustLoyaltyPoints(id);
  const setActive = useSetCustomerActive(id);
  const setConsent = useSetCustomerConsent(id);
  const [activeConfirmOpen, setActiveConfirmOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  // Enrollment modal state
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollPlanId, setEnrollPlanId] = useState<string>('');
  const [enrollPayment, setEnrollPayment] = useState<NonNullable<EnrollCustomerInput['payment_method']>>('CASH');
  const [cancelMembershipOpen, setCancelMembershipOpen] = useState(false);

  // Loyalty adjustment modal state (§8.2 — OWNER only)
  const [loyaltyModalOpen, setLoyaltyModalOpen] = useState(false);
  const [loyaltyType, setLoyaltyType] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [loyaltyPoints, setLoyaltyPoints] = useState('');
  const [loyaltyReason, setLoyaltyReason] = useState('');
  const [loyaltyErrors, setLoyaltyErrors] = useState<Record<string, string>>({});
  const [loyaltyConfirmOpen, setLoyaltyConfirmOpen] = useState(false);

  const customer = customerQ.data ?? null;
  // The loyalty summary only loads on the Loyalty tab; elsewhere the customer
  // row's balance is the source, so the adjust dialog, confirm and debit check agree.
  const loyaltyBalance = loyaltyQ.data?.balance ?? customer?.loyalty_points ?? 0;

  // Profile edit form (hydrated when data arrives)
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<ProfileFormState>({
    customer_type: 'INDIVIDUAL', first_name: '', last_name: '', phone: '', email: '', date_of_birth: undefined, anniversary: undefined, address: '', city: '', state: '', pincode: '', gstin: '', notes: '', tags: [], tagInput: '',
    company_name: '', company_address: '', contact_person: '', contact_email: '', contact_phone: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ProfileFormState, string>>>({});

  useEffect(() => {
    if (customer) {
      setForm({
        customer_type: customer.customer_type === 'CORPORATE' ? 'CORPORATE' : 'INDIVIDUAL',
        first_name: customer.first_name,
        last_name: customer.last_name ?? '',
        phone: customer.phone,
        email: customer.email ?? '',
        date_of_birth: customer.date_of_birth ? new Date(customer.date_of_birth) : undefined,
        anniversary: customer.anniversary ? new Date(customer.anniversary) : undefined,
        address: customer.address ?? '',
        city: customer.city ?? '',
        state: customer.state ?? '',
        pincode: customer.pincode ?? '',
        gstin: customer.gstin ?? '',
        notes: customer.notes ?? '',
        tags: customer.tags ?? [],
        tagInput: '',
        company_name: customer.company_name ?? '',
        company_address: customer.company_address ?? '',
        contact_person: customer.contact_person ?? '',
        contact_email: customer.contact_email ?? '',
        contact_phone: customer.contact_phone ?? '',
      });
    }
  }, [customer]);

  function addTag() {
    const tag = form.tagInput.trim();
    if (tag && !form.tags.includes(tag) && form.tags.length < 10) {
      setForm((f) => ({ ...f, tags: [...f.tags, tag], tagInput: '' }));
    }
  }

  async function handleSaveProfile() {
    const errs: typeof formErrors = {};
    const isCorporate = form.customer_type === 'CORPORATE';
    if (isCorporate) {
      if (!form.company_name.trim()) errs.company_name = 'Company name is required';
      if (form.contact_email && !isValidEmail(form.contact_email)) errs.contact_email = 'Invalid contact email';
    } else {
      if (!form.first_name.trim()) errs.first_name = 'First name is required';
    }
    const canonicalPhone = toE164(form.phone);
    if (!canonicalPhone || !isValidE164(canonicalPhone)) errs.phone = 'Invalid phone';
    if (form.email && !isValidEmail(form.email)) errs.email = 'Invalid email';
    if (form.gstin && tax.taxIdRegex && !tax.taxIdRegex.test(form.gstin.trim().toUpperCase())) errs.gstin = `Invalid ${tax.taxIdLabel ?? 'tax ID'}`;
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    function toYMD(d: Date | undefined): string | null {
      if (!d) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }

    try {
      // An emptied field is sent as null (tags as []) so the API clears it —
      // undefined is dropped from the JSON and the old value would stay.
      const base = {
        phone: canonicalPhone!,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
        tags: form.tags,
        customer_type: form.customer_type,
        // GSTIN applies to both types (shown for both) — always persist it.
        gstin: form.gstin.trim().toUpperCase() || null,
      };
      // Corporate: the record name is the company name (backend syncs it from
      // company_name); person + individual-address fields don't apply.
      await updateMutation.mutateAsync(isCorporate ? {
        ...base,
        company_name: form.company_name.trim(),
        company_address: form.company_address.trim() || undefined,
        contact_person: form.contact_person.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        contact_phone: toE164(form.contact_phone) || undefined,
      } : {
        ...base,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || undefined,
        date_of_birth: toYMD(form.date_of_birth),
        anniversary: toYMD(form.anniversary),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        pincode: form.pincode.trim() || null,
      });
      setIsEditing(false);
      toast({ variant: 'success', title: 'Customer updated' });
    } catch (err) {
      if (err instanceof ApiError) {
        toast({ variant: 'error', title: 'Update failed', description: err.message });
      }
    }
  }

  // Ctrl+S to save when editing. Read the latest values through a ref so
  // the keydown listener stays mounted once per page-view rather than
  // rebinding on every keystroke inside a form field.
  const saveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  saveRef.current = () => (isEditing ? handleSaveProfile() : Promise.resolve());
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void saveRef.current();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function openLoyaltyModal() {
    if (!isOwnerPlus) {
      toast({ variant: 'error', title: 'Permission denied', description: 'Only the Owner can adjust loyalty points' });
      return;
    }
    setLoyaltyType('CREDIT');
    setLoyaltyPoints('');
    setLoyaltyReason('');
    setLoyaltyErrors({});
    setLoyaltyModalOpen(true);
  }

  function validateLoyaltyForm(): boolean {
    const errs: Record<string, string> = {};
    const pts = Number(loyaltyPoints);
    if (!Number.isFinite(pts) || !Number.isInteger(pts) || pts <= 0) {
      errs.points = 'Points must be a positive whole number';
    }
    if (loyaltyType === 'DEBIT' && pts > loyaltyBalance) {
      errs.points = 'Insufficient points balance';
    }
    if (!loyaltyReason.trim()) errs.reason = 'Reason is required';
    else if (loyaltyReason.trim().length < 10) errs.reason = 'Reason must be at least 10 characters (mandatory)';
    else if (loyaltyReason.trim().length > 500) errs.reason = 'Reason must be at most 500 characters';
    setLoyaltyErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleLoyaltySubmit() {
    if (!validateLoyaltyForm()) return;
    setLoyaltyConfirmOpen(true);
  }

  async function handleLoyaltyConfirm() {
    setLoyaltyConfirmOpen(false);
    const pts = Number(loyaltyPoints);
    const amount = loyaltyType === 'DEBIT' ? -pts : pts;
    try {
      const summary = await adjustLoyalty.mutateAsync({ amount, reason: loyaltyReason.trim() });
      const verb = loyaltyType === 'CREDIT' ? 'credited to' : 'debited from';
      toast({
        variant: 'success',
        title: 'Points adjusted',
        description: `${pts} loyalty points ${verb} ${customer?.name ?? 'customer'}. New balance: ${summary.balance.toLocaleString('en-IN')}`,
      });
      setLoyaltyModalOpen(false);
    } catch (err) {
      if (err instanceof ApiError) toast({ variant: 'error', title: 'Adjustment failed', description: err.message });
    }
  }

  function handleSendInvoice() {
    navigate(`/communications/invoice-preview?customer=${id}`);
  }

  async function handleEnroll() {
    if (!enrollPlanId) return;
    try {
      await enrollMutation.mutateAsync({
        membership_plan_id: enrollPlanId,
        payment_method: enrollPayment,
      });
      toast({
        variant: 'success',
        title: 'Enrolled',
        description: 'Customer is now enrolled in the plan.',
      });
      setEnrollModalOpen(false);
      setEnrollPlanId('');
    } catch (err) {
      if (err instanceof ApiError) {
        toast({ variant: 'error', title: 'Could not enroll', description: err.message });
        return;
      }
      toast({ variant: 'error', title: 'Could not enroll', description: 'Please try again.' });
    }
  }

  async function handleCancelMembership() {
    try {
      await cancelMembership.mutateAsync(undefined);
      toast({
        variant: 'success',
        title: 'Membership cancelled',
        description: 'The customer is no longer enrolled.',
      });
      setCancelMembershipOpen(false);
    } catch (err) {
      const description = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Could not cancel', description });
    }
  }

  async function handleSetActive() {
    const reactivating = customer?.is_active === false;
    try {
      await setActive.mutateAsync(reactivating);
      toast({
        variant: 'success',
        title: reactivating ? 'Customer reactivated' : 'Customer deactivated',
        description: reactivating
          ? 'The customer can be found at the till again.'
          : 'The till will no longer find this customer. Bills, points and credit are kept.',
      });
      setActiveConfirmOpen(false);
    } catch (err) {
      const description = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: reactivating ? 'Could not reactivate' : 'Could not deactivate', description });
    }
  }

  const headerActions = (
    <>
      <Button variant="secondary" onClick={handleSendInvoice} className="!bg-white/15 hover:!bg-white/25 !text-white !border-white/30 backdrop-blur-sm">
        Send Invoice
      </Button>
      {isOwnerPlus ? (
        <Button variant="secondary" onClick={openLoyaltyModal} className="!bg-white/15 hover:!bg-white/25 !text-white !border-white/30 backdrop-blur-sm">
          Adjust Points
        </Button>
      ) : null}
      {isManagerPlus && customer ? (
        <Button variant="secondary" onClick={() => setActiveConfirmOpen(true)} className="!bg-white/15 hover:!bg-white/25 !text-white !border-white/30 backdrop-blur-sm">
          {customer.is_active === false ? 'Reactivate' : 'Deactivate'}
        </Button>
      ) : null}
      {isManagerPlus && customer && customer.is_active !== false ? (
        <Button variant="secondary" onClick={() => setMergeOpen(true)} className="!bg-white/15 hover:!bg-white/25 !text-white !border-white/30 backdrop-blur-sm">
          Merge into…
        </Button>
      ) : null}
      <Button
        variant="primary"
        onClick={() => setIsEditing((e) => !e)}
        className="!bg-white !text-violet-700 hover:!bg-white/90"
      >
        {isEditing ? 'Cancel Edit' : 'Edit Profile'}
      </Button>
    </>
  );

  if (customerQ.isLoading) {
    return (
      <>
        <Skeleton variant="rect" height={176} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={400} className="rounded-2xl" />
      </>
    );
  }

  if (!customer) {
    return (
      <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
        <EmptyState
          variant="page"
          title="Customer not found"
          description="This customer may have been deleted or the link is invalid."
          action={{ label: 'Back to customers', onClick: () => navigate('/customers') }}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <CustomerHeroProfile customer={customer} actions={headerActions} />

      <ReportKpiRow
        tiles={[
          { id: 'spent', title: 'Total Spent', value: formatPaise(customer.total_spent, currency), subLabel: customer.last_visit_at ? `Last visit ${formatShortDate(customer.last_visit_at)}` : 'No visits yet', tone: 'brand', icon: KpiIcons.rupee },
          { id: 'visits', title: 'Visits', value: customer.visit_count.toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.receipt },
          { id: 'aov', title: 'Avg Order', value: formatPaise(customer.avg_order_value, currency), tone: 'violet', icon: KpiIcons.cart },
          { id: 'loyalty', title: 'Loyalty Points', value: customer.loyalty_points.toLocaleString('en-IN'), subLabel: customer.loyalty_tier ?? (customer.is_loyalty_enrolled ? 'Enrolled' : 'Not enrolled'), tone: 'pink', icon: KpiIcons.star },
          {
            id: 'store-credit',
            title: 'Store Credit',
            value: formatPaise(customer.store_credit_balance ?? 0, currency),
            subLabel: (customer.store_credit_balance ?? 0) > 0
              ? 'Redeemable at next visit'
              : 'No balance',
            tone: 'success',
            icon: KpiIcons.rupee,
          },
        ]}
      />

      <div className="mb-5">
        <Tabs
          items={TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={activeTab}
          onChange={(tid) => {
            const params = new URLSearchParams(searchParams);
            params.set('tab', tid);
            setSearchParams(params);
          }}
          variant="pill"
          scrollable
        />
      </div>

      <div className="animate-fade-in">
        {activeTab === 'profile' ? (
          <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-6 sm:p-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Customer Type — editable while editing so an existing profile
                  can be switched between Individual and Company. */}
              <div className="sm:col-span-2 flex items-center gap-3">
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Customer Type</span>
                {isEditing ? (
                  <div className="flex gap-2">
                    {([['INDIVIDUAL', 'Individual'], ['CORPORATE', 'Company']] as const).map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, customer_type: val }))}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          form.customer_type === val
                            ? 'bg-violet-600 text-white border-violet-600'
                            : 'border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:border-violet-400'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Badge variant={form.customer_type === 'CORPORATE' ? 'brand' : 'neutral'} size="sm">
                    {form.customer_type === 'CORPORATE' ? 'Company' : 'Individual'}
                  </Badge>
                )}
              </div>

              {/* In view mode, only fields with a value are shown ("show only
                  the given data"). Editing reveals every field. */}
              {form.customer_type === 'CORPORATE' ? (
                <>
                  <div className="sm:col-span-2">
                    <Input label="Company Name" value={form.company_name} onChange={(v) => setForm((f) => ({ ...f, company_name: v }))} disabled={!isEditing} error={formErrors.company_name} />
                  </div>
                  <Input label="Phone" variant="phone" defaultDial={dial} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} disabled={!isEditing} error={formErrors.phone} />
                  {(isEditing || form.email) && (
                    <Input label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} disabled={!isEditing} error={formErrors.email} />
                  )}
                  {(isEditing || form.company_address) && (
                    <div className="sm:col-span-2">
                      <TextArea label="Company Address" value={form.company_address} onChange={(v) => setForm((f) => ({ ...f, company_address: v }))} disabled={!isEditing} maxLength={500} rows={2} />
                    </div>
                  )}
                  {(isEditing || form.contact_person) && (
                    <Input label="Contact Person" value={form.contact_person} onChange={(v) => setForm((f) => ({ ...f, contact_person: v }))} disabled={!isEditing} />
                  )}
                  {(isEditing || form.contact_email) && (
                    <Input label="Contact Email" type="email" value={form.contact_email} onChange={(v) => setForm((f) => ({ ...f, contact_email: v }))} disabled={!isEditing} error={formErrors.contact_email} />
                  )}
                  {(isEditing || form.contact_phone) && (
                    <Input label="Contact Phone" variant="phone" defaultDial={dial} value={form.contact_phone} onChange={(v) => setForm((f) => ({ ...f, contact_phone: v }))} disabled={!isEditing} />
                  )}
                </>
              ) : (
                <>
                  <Input label="First Name" value={form.first_name} onChange={(v) => setForm((f) => ({ ...f, first_name: v }))} disabled={!isEditing} error={formErrors.first_name} />
                  {(isEditing || form.last_name) && (
                    <Input label="Last Name" value={form.last_name} onChange={(v) => setForm((f) => ({ ...f, last_name: v }))} disabled={!isEditing} />
                  )}
                  <Input label="Phone" variant="phone" defaultDial={dial} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} disabled={!isEditing} error={formErrors.phone} />
                  {(isEditing || form.email) && (
                    <Input label="Email" type="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} disabled={!isEditing} error={formErrors.email} />
                  )}
                  {(isEditing || form.date_of_birth) && (
                    <DatePicker label="Date of Birth" variant="single" value={form.date_of_birth} onChange={(v) => setForm((f) => ({ ...f, date_of_birth: v }))} disabled={!isEditing} maxDate={new Date()} />
                  )}
                  {(isEditing || form.anniversary) && (
                    <DatePicker label="Anniversary" variant="single" value={form.anniversary} onChange={(v) => setForm((f) => ({ ...f, anniversary: v }))} disabled={!isEditing} />
                  )}
                  {(isEditing || form.address) && (
                    <div className="sm:col-span-2">
                      <TextArea label="Address" value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))} disabled={!isEditing} maxLength={500} rows={2} />
                    </div>
                  )}
                  {(isEditing || form.city) && (
                    <Input label="City" value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))} disabled={!isEditing} />
                  )}
                  {(isEditing || form.state) && (
                    <Input label="State" value={form.state} onChange={(v) => setForm((f) => ({ ...f, state: v }))} disabled={!isEditing} />
                  )}
                  {(isEditing || form.pincode) && (
                    <Input label="Pincode" value={form.pincode} onChange={(v) => setForm((f) => ({ ...f, pincode: v }))} disabled={!isEditing} />
                  )}
                </>
              )}
              {tax.taxIdLabel && (isEditing || form.gstin) && (
                <Input label={tax.taxIdLabel} value={form.gstin} onChange={(v) => setForm((f) => ({ ...f, gstin: v }))} disabled={!isEditing} error={formErrors.gstin} />
              )}
              {(isEditing || form.notes) && (
                <div className="sm:col-span-2">
                  <TextArea label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} disabled={!isEditing} maxLength={1000} rows={3} />
                </div>
              )}
              {/* Tags */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-violet-100 dark:bg-violet-900/30 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300 ring-1 ring-violet-200 dark:ring-violet-700">
                      {tag}
                      {isEditing ? (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((_, idx) => idx !== i) }))} className="ml-0.5 text-violet-400 hover:text-violet-600 dark:hover:text-violet-200 transition-colors" aria-label={`Remove tag ${tag}`}>&times;</button>
                      ) : null}
                    </span>
                  ))}
                  {form.tags.length === 0 ? <span className="text-xs text-neutral-400">No tags</span> : null}
                </div>
                {isEditing ? (
                  <div className="flex gap-2">
                    <Input placeholder={form.tags.length >= 10 ? 'Max 10 tags' : 'Add tag'} value={form.tagInput} onChange={(v) => setForm((f) => ({ ...f, tagInput: v }))} onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addTag();
                      }
                    }} disabled={form.tags.length >= 10} />
                    <Button type="button" variant="secondary" disabled={!form.tagInput.trim() || form.tags.length >= 10} onClick={addTag}>Add</Button>
                  </div>
                ) : null}
              </div>
            </div>
            {isEditing ? (
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleSaveProfile} loading={updateMutation.isPending}>Save Changes</Button>
              </div>
            ) : null}
            {/* Marketing consent (CU4-003, decision C9): on = receives marketing.
                Saved at once, not with the profile form. */}
            <div className="mt-6 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-4 ring-1 ring-indigo-100 dark:ring-indigo-800/30">
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Marketing messages</p>
              <p className="mt-1 mb-3 text-xs text-neutral-500 dark:text-neutral-400">
                Campaigns, Deal of the Day and re-order links. Bills and receipts are always sent.
                {customer.consent_changed_at ? ` Last changed ${formatShortDate(customer.consent_changed_at)}.` : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {([
                  ['email_opt_out', 'Email'],
                  ['whatsapp_opt_out', 'WhatsApp'],
                  ['sms_opt_out', 'SMS'],
                ] as const).map(([field, label]) => (
                  <Toggle
                    key={field}
                    label={label}
                    description={customer[field] ? 'Opted out' : 'Opted in'}
                    checked={!customer[field]}
                    disabled={setConsent.isPending}
                    onChange={(on) => {
                      setConsent.mutate({ [field]: !on }, {
                        onSuccess: () => toast({ variant: 'success', title: `${label} marketing ${on ? 'turned on' : 'turned off'}` }),
                        onError: (err) => toast({ variant: 'error', title: 'Could not save', description: err instanceof ApiError ? err.message : undefined }),
                      });
                    }}
                  />
                ))}
              </div>
            </div>
          </section>
        ) : activeTab === 'purchases' ? (
          <CustomerPurchaseTable
            rows={purchasesQ.data?.data ?? []}
            total={purchasesQ.data?.total}
            page={purchasesPage}
            loading={purchasesQ.isFetching}
            onPageChange={setPurchasesPage}
          />
        ) : activeTab === 'loyalty' ? (
          <>
            {loyaltyQ.data ? (
              <div className="rounded-2xl bg-gradient-to-br from-pink-50 via-violet-50 to-brand-50 dark:from-pink-900/20 dark:via-violet-900/20 dark:to-brand-900/20 p-5 ring-1 ring-violet-100 dark:ring-violet-800/30 mb-6 animate-slide-up">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Balance</p>
                    <p className="mt-1 text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{loyaltyQ.data.balance.toLocaleString('en-IN')} pts</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Lifetime Earned</p>
                    <p className="mt-1 text-lg font-semibold text-neutral-900 dark:text-white tabular-nums">{loyaltyQ.data.lifetime_earned.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Lifetime Redeemed</p>
                    <p className="mt-1 text-lg font-semibold text-neutral-900 dark:text-white tabular-nums">{loyaltyQ.data.lifetime_redeemed.toLocaleString('en-IN')}</p>
                  </div>
                </div>
                {loyaltyQ.data.expiring_points ? (
                  <p className="mt-3 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-3 py-1 text-xs font-semibold text-amber-800 dark:text-amber-300">
                    {loyaltyQ.data.expiring_points.amount.toLocaleString('en-IN')} points expire on{' '}
                    {calendarDay(loyaltyQ.data.expiring_points.expiry_date).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  </p>
                ) : null}
                <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">{loyaltyQ.data.earn_rate}</p>
              </div>
            ) : null}
            <LoyaltyLedgerTable rows={loyaltyQ.data?.ledger ?? []} loading={loyaltyQ.isFetching} />
          </>
        ) : activeTab === 'credits' ? (
          <CreditLedgerTable rows={creditsQ.data ?? []} loading={creditsQ.isFetching} />
        ) : activeTab === 'vouchers' ? (
          <VoucherRedemptionTable
            rows={vouchersQ.data?.data ?? []}
            total={vouchersQ.data?.total}
            loading={vouchersQ.isFetching}
          />
        ) : activeTab === 'memberships' ? (
          <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-6 sm:p-7 animate-slide-up">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100">
                  Memberships
                </h2>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  Enroll this customer in a subscription tier or cancel an existing one.
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => setEnrollModalOpen(true)}
                disabled={
                  (activePlansQ.data?.data.length ?? 0) === 0 ||
                  Boolean(activeMembershipQ.data) ||
                  activeMembershipQ.isFetching
                }
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
              >
                + Enroll in Plan
              </Button>
            </div>

            {activePlansQ.isFetching || activeMembershipQ.isFetching ? (
              <div className="mt-6 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
                ))}
              </div>
            ) : activeMembershipQ.data ? (
              /* Current active enrollment */
              (() => {
                const m = activeMembershipQ.data;
                const expiresAt = new Date(m.expires_at);
                const daysLeft = Math.max(
                  0,
                  Math.ceil((expiresAt.getTime() - Date.now()) / 86_400_000),
                );
                return (
                  <div className="mt-6 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-5 ring-1 ring-amber-100 dark:ring-amber-800/30">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <MembershipTierPill tier={m.tier_level} size="md" />
                        <div className="min-w-0">
                          <p className="text-base font-bold text-neutral-900 dark:text-neutral-100 truncate">
                            {m.plan_name}
                          </p>
                          <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300">
                            Enrolled {formatShortDate(m.enrolled_at)} · {formatPaise(m.fee_paid, currency)} paid
                          </p>
                        </div>
                      </div>
                      <Badge variant="success" size="md">
                        <span className="flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {m.status}
                        </span>
                      </Badge>
                    </div>

                    <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                          Valid Until
                        </dt>
                        <dd className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                          {formatShortDate(m.expires_at)}
                        </dd>
                        <dd className="text-[11px] text-neutral-500 dark:text-neutral-400">
                          {daysLeft} day{daysLeft === 1 ? '' : 's'} left
                        </dd>
                      </div>
                      {m.discount_percentage != null ? (
                        <div>
                          <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                            Discount
                          </dt>
                          <dd className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                            {m.discount_percentage}%
                          </dd>
                        </div>
                      ) : null}
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                          Auto-Renewal
                        </dt>
                        <dd className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                          {m.auto_renewal ? 'On' : 'Off'}
                        </dd>
                      </div>
                    </dl>

                    <div className="mt-5 flex justify-end">
                      <Button
                        variant="ghost"
                        onClick={() => setCancelMembershipOpen(true)}
                        className="!text-danger-600 hover:!bg-danger-50 dark:hover:!bg-danger-900/20"
                      >
                        Cancel Membership
                      </Button>
                    </div>
                  </div>
                );
              })()
            ) : (activePlansQ.data?.data.length ?? 0) === 0 ? (
              /* No active plans exist at all */
              <div className="mt-6 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-6 ring-1 ring-amber-100 dark:ring-amber-800/30 text-center">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  No active plans yet
                </p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {isOwnerPlus
                    ? 'Create a membership plan first, then come back to enroll this customer.'
                    : 'Ask the owner to create a membership plan, then come back to enroll this customer.'}
                </p>
                {/* Plan management is owner-only — don't link a manager to a page they cannot open. */}
                {isOwnerPlus ? (
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/memberships')}
                    className="mt-4"
                  >
                    Open Membership Plans
                  </Button>
                ) : null}
              </div>
            ) : (
              /* Plans exist but customer is not enrolled */
              <div className="mt-6 rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 p-6 text-center">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  Not enrolled in any plan
                </p>
                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                  {activePlansQ.data?.data.length.toLocaleString('en-IN')} active plan
                  {(activePlansQ.data?.data.length ?? 0) === 1 ? '' : 's'} available — use{' '}
                  <span className="font-medium">Enroll in Plan</span> to subscribe this customer.
                </p>
              </div>
            )}
          </section>
        ) : (
          <CommunicationLogTable rows={commsQ.data ?? []} loading={commsQ.isFetching} />
        )}
      </div>

      {isEditing ? (
        <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 text-right">
          Press <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">Ctrl+S</kbd> to save
        </p>
      ) : null}

      <Modal
        open={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        title="Enroll in Membership Plan"
        allowOverflow
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEnrollModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEnroll}
              loading={enrollMutation.isPending}
              disabled={!enrollPlanId}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            >
              Confirm Enrollment
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <div>
            <label
              htmlFor="enroll-plan-select"
              className="block text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5"
            >
              Plan
            </label>
            <Select
              id="enroll-plan-select"
              options={[
                { value: '', label: 'Choose a plan…' },
                ...(activePlansQ.data?.data ?? []).map((p) => ({
                  value: p.id,
                  label: `${p.name} — ${formatTierLabel(p.tier_level)} · ${formatPaise(p.fee, currency)} / ${formatDuration(p.duration_days).short}`,
                })),
              ]}
              value={enrollPlanId}
              onChange={(v) => setEnrollPlanId(typeof v === 'string' ? v : '')}
            />
          </div>

          {enrollPlanId ? (
            (() => {
              const plan = activePlansQ.data?.data.find((p) => p.id === enrollPlanId);
              if (!plan) return null;
              return (
                <div className="rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 ring-1 ring-amber-100 dark:ring-amber-800/30">
                  <div className="flex items-center gap-3">
                    <MembershipTierPill tier={plan.tier_level} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">
                        {plan.name}
                      </p>
                      <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300 tabular-nums">
                        {formatPaise(plan.fee, currency)} · {formatDuration(plan.duration_days).long}
                      </p>
                    </div>
                  </div>
                  {plan.description ? (
                    <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
                      {plan.description}
                    </p>
                  ) : null}
                </div>
              );
            })()
          ) : null}

          <RadioGroup
            label="Payment Method"
            value={enrollPayment}
            onChange={(v) => setEnrollPayment(v as NonNullable<EnrollCustomerInput['payment_method']>)}
            variant="horizontal"
            options={[
              { value: 'CASH', label: 'Cash' },
              { value: 'UPI', label: 'UPI' },
              { value: 'CARD', label: 'Card' },
              { value: 'WALLET', label: 'Wallet' },
            ]}
          />
        </div>
      </Modal>

      {/* Loyalty Adjustment Modal (§8.2 — OWNER only) */}
      <Modal
        open={loyaltyModalOpen}
        onClose={() => setLoyaltyModalOpen(false)}
        title="Adjust Loyalty Points"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setLoyaltyModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleLoyaltySubmit} loading={adjustLoyalty.isPending}>
              {loyaltyType === 'CREDIT' ? 'Credit Points' : 'Debit Points'}
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          {/* Current balance */}
          <div className="rounded-xl bg-gradient-to-br from-pink-50 via-violet-50 to-brand-50 dark:from-pink-900/20 dark:via-violet-900/20 dark:to-brand-900/20 p-4 ring-1 ring-violet-100 dark:ring-violet-800/30" aria-live="polite">
            <p className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">Current Balance</p>
            <p className="mt-1 text-2xl font-bold text-neutral-900 dark:text-white font-mono tabular-nums">{loyaltyBalance.toLocaleString('en-IN')} pts</p>
          </div>

          <RadioGroup
            label="Adjustment Type"
            value={loyaltyType}
            onChange={(v) => setLoyaltyType(v as 'CREDIT' | 'DEBIT')}
            variant="horizontal"
            error={loyaltyErrors.type}
            options={[
              { value: 'CREDIT', label: 'Credit (add)' },
              { value: 'DEBIT', label: 'Debit (deduct)' },
            ]}
          />

          <Input
            label="Points"
            type="number"
            min={1}
            value={loyaltyPoints}
            onChange={(v) => { setLoyaltyPoints(v); setLoyaltyErrors((e) => ({ ...e, points: undefined as unknown as string })); }}
            error={loyaltyErrors.points}
            placeholder="Enter points"
          />

          <TextArea
            label="Reason"
            required
            value={loyaltyReason}
            onChange={(v) => { setLoyaltyReason(v); setLoyaltyErrors((e) => ({ ...e, reason: undefined as unknown as string })); }}
            error={loyaltyErrors.reason}
            placeholder="Mandatory justification for audit trail (min 10 characters)"
            rows={3}
            maxLength={500}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={loyaltyConfirmOpen}
        title="Confirm Points Adjustment"
        description={`Are you sure you want to ${loyaltyType === 'CREDIT' ? 'credit' : 'debit'} ${loyaltyPoints || 0} points? Current balance: ${loyaltyBalance.toLocaleString('en-IN')}.`}
        confirmLabel={loyaltyType === 'CREDIT' ? 'Credit Points' : 'Debit Points'}
        variant={loyaltyType === 'DEBIT' ? 'danger' : 'warning'}
        onConfirm={handleLoyaltyConfirm}
        onCancel={() => setLoyaltyConfirmOpen(false)}
        loading={adjustLoyalty.isPending}
      />

      <ConfirmDialog
        open={cancelMembershipOpen}
        title="Cancel Membership"
        description={`Cancel ${activeMembershipQ.data?.plan_name ?? 'this'} membership for this customer? They will lose member benefits immediately. This cannot be undone.`}
        confirmLabel="Cancel Membership"
        variant="danger"
        onConfirm={handleCancelMembership}
        onCancel={() => setCancelMembershipOpen(false)}
        loading={cancelMembership.isPending}
      />

      <ConfirmDialog
        open={activeConfirmOpen}
        title={customer.is_active === false ? 'Reactivate Customer' : 'Deactivate Customer'}
        description={customer.is_active === false
          ? `Reactivate ${customer.name}? The till will find them again.`
          : `Deactivate ${customer.name}? The till will no longer find them. Their bills, loyalty points and store credit are kept, and you can reactivate them later.`}
        confirmLabel={customer.is_active === false ? 'Reactivate' : 'Deactivate'}
        variant={customer.is_active === false ? 'warning' : 'danger'}
        onConfirm={handleSetActive}
        onCancel={() => setActiveConfirmOpen(false)}
        loading={setActive.isPending}
      />

      {mergeOpen ? (
        <MergeCustomerModal
          open
          onClose={() => setMergeOpen(false)}
          customer={customer}
          onMerged={(targetId) => {
            setMergeOpen(false);
            toast({ variant: 'success', title: 'Customers merged', description: `${customer.name} was merged and deactivated.` });
            navigate(`/customers/${targetId}`);
          }}
        />
      ) : null}
    </div>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <PageContainer title="Customer" breadcrumbs={[{ label: 'Customers', href: '/customers' }]}>
        <div className="p-6 text-neutral-500">Missing customer id.</div>
      </PageContainer>
    );
  }
  return (
    <PageContainer
      title="Customer"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Customers', href: '/customers' },
        { label: 'Customer Profile' },
      ]}
    >
      <ErrorBoundary resetKey={`customer-${id}`}>
        <CustomerDetailContent id={id} />
      </ErrorBoundary>
    </PageContainer>
  );
}
